import { Button, IconButton } from '@droplet-tech-code/core-elements/module/ui/Button';
import { Modal, useModal } from '@droplet-tech-code/core-elements/module/ui/Modal';
import { HStack, VStack } from '@droplet-tech-code/core-elements/module/ui/Stack';
import { Text } from '@droplet-tech-code/core-elements/module/ui/Text';
import { View } from '@droplet-tech-code/core-elements/module/ui/View';
import { dayjs } from '@droplet-tech-code/core-elements/module/utils/utils.date';
import { SupportRequestBackendClient } from '@naus-code/naus-admin-types';
import { useState } from 'react';
import { supportRequestActionsApi } from '~/screens/Support/Requests/supportRequestActions.api';

import { translate } from '~/utils/translation.utils';
import { VirtualInventoryPayloadDetailRequestOption } from '@naus-code/naus-client-types';
import { purchaseApi } from '~/screens/Support/Purchases/Purchases.api';
import { AppText } from '~/components/Text';
import { LoadingIndicator } from '@droplet-tech-code/core-elements/module/ui/Loading';
import { SquarePill } from '~/screens/Support/Requests/Pending/Flipper-SupportRequestsPending/Flipper.SupportRequestPending.ListItem';
import {
  getVIOptionsFromSR,
  VirtualInventoryErrorRequestOption,
} from '../utils/SupportRequestData';
import { ModalScrollView } from '~/components/Modals/Modal.utils';
import { JSONView } from '~/components/JsonView';
import { Divider } from '@droplet-tech-code/core-elements/module/ui/Divider/Divider';
import { supportRequestApi } from '~/screens/Support/Requests/SupportRequests.api';
import { handleResponse } from '@droplet-tech-code/core-elements/module/utils/error';
import { onReceiveValue } from '../utils/utils';

const VirtualInventoryPayloadModalKey = 'VirtualInventoryPayloadModalKey';

export const VirtualInventoryPayloadSection = ({
  supportId,
  check,
}: {
  supportId: string;
  check: string;
}) => {
  const { data, isLoading } = supportRequestApi.useGetSupportRequestQuery(supportId);

  const { closeModal } = useModal();
  const [currentIndex, setCurrentIndex] = useState(0);

  const [approveChanges, { isLoading: isLoadingApprove }] =
    supportRequestActionsApi.useApproveVIChangesMutation();

  const [rejectChanges, { isLoading: isLoadingReject }] =
    supportRequestActionsApi.useRejectVIChangesMutation();

  if (isLoading) {
    return <LoadingIndicator />;
  }
  if (!data) {
    return null;
  }
  const supportRequest = data.supportRequest;
  const virtualInventoryPayload = getVIOptionsFromSR(supportRequest)?.viPayload;
  if (!virtualInventoryPayload || virtualInventoryPayload.length === 0) return null;
  const isOutDated = virtualInventoryPayload[currentIndex].isOutDated as
    | boolean
    | undefined;

  return (
    <VStack bg="success-base" br={20}>
      <VStack style={{ flex: undefined }} p="2.5">
        <HStack space="1" justify="flex-start">
          <AppText.Body2Medium flex={1}>
            {translate('supportRequests.virtualInventory.instruction')}
          </AppText.Body2Medium>
          <IconButton
            icon="chevron-left"
            onPress={() => {
              setCurrentIndex(Math.max(0, currentIndex - 1));
            }}
            disabled={currentIndex === 0}
          />

          <IconButton
            icon="chevron-right"
            onPress={() => {
              setCurrentIndex(
                Math.min(virtualInventoryPayload.length - 1, currentIndex + 1),
              );
            }}
            disabled={currentIndex === virtualInventoryPayload.length - 1}
          />
        </HStack>
        <VirtualInventoryPayloadComponent
          isOutDated={isOutDated}
          virtualInventoryPayload={virtualInventoryPayload[currentIndex]}
        />
      </VStack>
      <Modal.Footer
        divider
        onCancel={() => {
          closeModal(VirtualInventoryPayloadModalKey);
        }}
      >
        <Button
          isLoading={isLoadingApprove}
          style={{ flex: undefined }}
          variant="text"
          disabled={isOutDated}
          onPress={() => {
            closeModal(VirtualInventoryPayloadModalKey);

            approveChanges({
              supportId,
              check,
              viToken: virtualInventoryPayload[currentIndex].virtualInventoryToken,
            }).then((response) => {
              handleResponse({
                response,
              });
            });
          }}
        >
          {translate('common.approve')}
        </Button>
        <Button
          isLoading={isLoadingReject}
          variant="text"
          color="error"
          style={{ flex: undefined }}
          disabled={isOutDated}
          onPress={() => {
            closeModal(VirtualInventoryPayloadModalKey);
            rejectChanges({
              supportId,
              check,
              viToken: virtualInventoryPayload[currentIndex].virtualInventoryToken,
            }).then((response) => {
              handleResponse({
                response,
              });
            });
          }}
        >
          {translate('common.reject')}
        </Button>
        <View flex={5} />
      </Modal.Footer>
    </VStack>
  );
};

export const VirtualInventoryErrorSection = ({
  supportId,
  check,
}: {
  supportId: string;
  check: string;
}) => {
  const { data, isLoading } = supportRequestApi.useGetSupportRequestQuery(supportId);

  const { closeModal } = useModal();
  const [currentIndex, setCurrentIndex] = useState(0);

  const [resolveError, { isLoading: isLoadingResolveError }] =
    supportRequestActionsApi.useResolveVIErrorMutation();

  if (isLoading) {
    return <LoadingIndicator />;
  }
  if (!data) {
    return null;
  }
  const supportRequest = data.supportRequest;
  const reverseViError = getVIOptionsFromSR(supportRequest)?.errorPayload;
  if (!reverseViError || reverseViError.length === 0) return null;
  const viError = [...reverseViError].reverse();
  const isOutDated = viError[currentIndex].isOutDated as boolean | undefined;

  return (
    <VStack bg="error-base" br={20}>
      <VStack style={{ flex: undefined }} p="2.5">
        <HStack space="1" justify="flex-start" bg="error-base">
          <AppText.Body2Medium flex={1}>
            {translate('supportRequests.virtualInventory.error')}
          </AppText.Body2Medium>
          <IconButton
            icon="chevron-left"
            onPress={() => {
              setCurrentIndex(Math.max(0, currentIndex - 1));
            }}
            disabled={currentIndex === 0}
          />

          <IconButton
            icon="chevron-right"
            onPress={() => {
              setCurrentIndex(Math.min(viError.length - 1, currentIndex + 1));
            }}
            disabled={currentIndex === viError.length - 1}
          />
        </HStack>
        <VirtualInventoryErrorComponent
          isOutDated={isOutDated}
          virtualInventoryErrorPayload={viError[currentIndex]}
        />
      </VStack>
      <Modal.Footer
        divider
        onCancel={() => {
          closeModal(VirtualInventoryPayloadModalKey);
        }}
      >
        <Button
          isLoading={isLoadingResolveError}
          style={{ flex: undefined }}
          variant="text"
          disabled={isOutDated}
          onPress={() => {
            resolveError({
              supportId,
              check,
              errorId: viError[currentIndex].id,
            });
          }}
        >
          {translate('supportRequests.virtualInventory.resolve')}
        </Button>
        <View flex={5} />
      </Modal.Footer>
    </VStack>
  );
};

export const useVirtualInventoryPayload = () => {
  const { openModal } = useModal();
  return {
    open: (supportRequest: SupportRequestBackendClient, check: string) => {
      openModal(
        <VirtualInventoryOptionsModal supportId={supportRequest._id} check={check} />,
        {
          key: VirtualInventoryPayloadModalKey,
          type: 'action-sheet-full',
        },
      );
    },
  };
};

const VirtualInventoryOptionsModal = ({
  supportId,
  check,
}: {
  supportId: string;
  check: string;
}) => {
  const { closeModal } = useModal();

  return (
    <Modal.Container style={{ flex: undefined, width: 600 }}>
      <Modal.Header
        title={translate('supportRequests.virtualInventoryPayload')}
        divider
        onPress={() => {
          closeModal(VirtualInventoryPayloadModalKey);
        }}
      ></Modal.Header>
      <Modal.Body>
        <VirtualInventoryPayloadSection supportId={supportId} check={check} />
        <Divider />
        <VirtualInventoryErrorSection supportId={supportId} check={check} />
      </Modal.Body>
    </Modal.Container>
  );
};

const VirtualInventoryPayloadComponent = ({
  virtualInventoryPayload,
  isOutDated,
}: {
  virtualInventoryPayload: VirtualInventoryPayloadDetailRequestOption;
  isOutDated?: boolean;
}) => {
  const price = virtualInventoryPayload.details.price;
  console.log(price);
  //@ts-ignore
  const providerCode = virtualInventoryPayload.providerCode;
  const payloadEntries = Object.entries(virtualInventoryPayload.details.purchaseChanges);

  return (
    <VStack space="2.5" style={{ opacity: isOutDated ? 0.5 : 1 }}>
      <HStack space="4">
        <AppText.Body1SemiBold>{`Provider Code ${providerCode}`}</AppText.Body1SemiBold>
        {isOutDated ? (
          <SquarePill text="Outdated" fColor="#9a0202ff" bgColor="#ffc8c8ff" />
        ) : null}
      </HStack>
      <ValueComparing
        title={translate('supportRequests.virtualInventory.price')}
        valueOld={`${onReceiveValue(price.old.value)} ${price.old.currency}`}
        valueNew={`${onReceiveValue(price.new.value)} ${price.new.currency}`}
      />
      {payloadEntries.map(([purchaseId, payloadChanges]) => (
        <PayloadEntry purchaseId={purchaseId} payloadChanges={payloadChanges} />
      ))}
    </VStack>
  );
};

const VirtualInventoryErrorComponent = ({
  virtualInventoryErrorPayload,
  isOutDated,
}: {
  virtualInventoryErrorPayload: VirtualInventoryErrorRequestOption;
  isOutDated?: boolean;
}) => {
  const providerCode = virtualInventoryErrorPayload.providerCode;
  const { type, virtualInventoryToken, meta, timestamp, description } =
    virtualInventoryErrorPayload;

  return (
    <VStack space="2.5" style={{ opacity: isOutDated ? 0.5 : 1 }}>
      <HStack space="4">
        <AppText.Body1SemiBold>{`Provider Code ${providerCode}`}</AppText.Body1SemiBold>
        {isOutDated ? (
          <SquarePill text="Outdated" fColor="#9a0202ff" bgColor="#ffc8c8ff" />
        ) : null}
      </HStack>
      <VStack space="4">
        <ValueDisplay
          title={translate('supportRequests.virtualInventory.type')}
          value={type}
        />
        <ValueDisplay
          title={translate('supportRequests.virtualInventory.token')}
          value={virtualInventoryToken}
        />
        <ValueDisplay
          title={translate('supportRequests.virtualInventory.timestamp')}
          value={timestamp}
          isDate
        />
        <ValueDisplay
          title={translate('supportRequests.virtualInventory.description')}
          value={description}
        />
        {meta ? (
          <ModalScrollView>
            <JSONView data={meta} collapsed />
          </ModalScrollView>
        ) : undefined}
      </VStack>
      <AppText.Small></AppText.Small>
    </VStack>
  );
};

const PayloadEntry = ({
  purchaseId,
  payloadChanges,
}: {
  purchaseId: string;
  payloadChanges: VirtualInventoryPayloadDetailRequestOption['details']['purchaseChanges'][0];
}) => {
  const { departDateTime, arrivalDateTime, accommodationDetails } = payloadChanges;
  const { data: purchase, isLoading } = purchaseApi.useGetPurchaseQuery(purchaseId);
  if (isLoading) {
    return <LoadingIndicator />;
  }
  return (
    <VStack space="1">
      <AppText.Body2Medium>
        {purchase
          ? `${purchase.purchase.originDisplay} - ${purchase.purchase.destinationDisplay} : ${dayjs(purchase.purchase.startTimestamp).toString()}`
          : `purchase ${purchaseId} not found`}
      </AppText.Body2Medium>

      {[
        departDateTime ? (
          <ValueComparing
            title={translate('supportRequests.virtualInventory.departure')}
            valueOld={departDateTime.old}
            valueNew={departDateTime.new}
            isDate
          />
        ) : null,
        arrivalDateTime ? (
          <ValueComparing
            title={translate('supportRequests.virtualInventory.arrival')}
            valueOld={arrivalDateTime.old}
            valueNew={arrivalDateTime.new}
            isDate
          />
        ) : null,
        accommodationDetails.new.length ? (
          <ValueComparingAccomodations
            title={translate('supportRequests.virtualInventory.accommodation')}
            valueOld={accommodationDetails.old}
            valueNew={accommodationDetails.new}
          />
        ) : null,
      ].filter((component) => !!component)}
    </VStack>
  );
};

const ValueComparing = ({
  title,
  valueOld,
  valueNew,
  isDate,
}: {
  title: string;
  valueOld: any;
  valueNew: any;
  isDate?: boolean;
}) => {
  if (!valueOld || !valueNew) {
    return;
  }
  if (typeof valueOld === 'object' || typeof valueNew === 'object')
    if (!valueNew.length || !valueOld.length) {
      return;
    }

  const parsedValueOld = isDate ? dayjs(valueOld).toString() : valueOld + '';
  const parsedValueNew = isDate ? dayjs(valueNew).toString() : valueNew + '';

  let formatting;
  if (parsedValueNew !== parsedValueOld) {
    formatting = {
      color: 'red',
      textDecorationColor: 'red',
      textDecorationLine: 'line-through',
    };
  }
  return (
    <VStack space="1">
      <Text.Body2Medium>{title}</Text.Body2Medium>
      <HStack flex={1}>
        <Text.Small style={formatting}>{parsedValueOld}</Text.Small>
        <View flex={1} />
        <Text.Small bold color="success">
          {parsedValueNew}
        </Text.Small>
      </HStack>
    </VStack>
  );
};

const ValueComparingAccomodations = ({
  title,
  valueOld,
  valueNew,
}: {
  title: string;
  valueOld: { quantity: number; description: string }[];
  valueNew: { quantity: number; description: string }[];
}) => {
  // if (!valueOld || !valueNew) {
  //   return;
  // }
  const accomodationOldProcessed = valueOld.length
    ? valueOld
    : [{ description: 'Standard' }];

  const accomodationNewProcessed = valueNew.length
    ? valueNew
    : [{ description: 'Standard' }];

  return (
    <VStack space="1">
      <Text.Body2Medium>{title}</Text.Body2Medium>
      <HStack flex={1}>
        <VStack>
          <Text.Small>{'Old'}</Text.Small>
          {accomodationOldProcessed.map(({ quantity, description }) => {
            return (
              <VStack>
                <Text.Small>{`${quantity || 'N/A'} x ${description}`}</Text.Small>
              </VStack>
            );
          })}
        </VStack>
        <View flex={1} />
        <VStack>
          <Text.Small>{'New'}</Text.Small>
          {accomodationNewProcessed.map(({ quantity, description }) => {
            return (
              <VStack>
                <Text.Small>{`${quantity || 'N/A'} x ${description}`}</Text.Small>
              </VStack>
            );
          })}
        </VStack>
      </HStack>
    </VStack>
  );
};

const ValueDisplay = ({
  title,
  value,
  isDate,
}: {
  title: string;
  value?: string;
  isDate?: boolean;
}) => {
  if (!value) {
    return null;
  }
  const parsedValue = isDate ? dayjs(value).toString() : value + '';

  return (
    <VStack space="1">
      <Text.Body2Medium>{title}</Text.Body2Medium>
      <HStack flex={1}>
        <Text.Small>{parsedValue}</Text.Small>
        <View flex={1} />
      </HStack>
    </VStack>
  );
};
